import { useEffect } from "react";
import { useIntl } from "react-intl";

import type { FolderItem } from "~/client/components/search/hooks/useFolderSearchResult";
import type { MemberItem } from "~/client/components/search/hooks/useMemberSearchResult";
import type { NoteAndCommentItem } from "~/client/components/search/hooks/useNoteAndCommentSearchResult";
import type { SearchFilterSetItem } from "~/client/components/search/hooks/useSavedSearchFilterSets";
import { FolderList } from "~/client/components/search/IncrementalSearchResult/FolderList";
import { MemberList } from "~/client/components/search/IncrementalSearchResult/MemberList";
import {
  NoteAndCommentList,
  noteAndCommentListTitle,
} from "~/client/components/search/IncrementalSearchResult/NoteAndCommentList";
import { SavedSearchFilterSetList } from "~/client/components/search/IncrementalSearchResult/SavedSearchFilterSetList";
import { SearchBoxDropdownSection } from "~/client/components/search/SearchBoxDropdown/SearchBoxDropdownSection";
import { WithLoading } from "~/client/components/search/SearchBoxDropdown/WithLoading";
import { useSelectListItemByKey } from "~/client/hooks/useSelectListItemByKey";
import type { ConnectionWithTotalCount } from "~/client/utils/relay";

interface Props {
  query: string;
  noteAndComment: {
    items: ConnectionWithTotalCount<NoteAndCommentItem>;
    onViewAll: () => void;
    isLoading: boolean;
  };
  folder: {
    items: ConnectionWithTotalCount<FolderItem>;
    onViewAll: () => void;
    isLoading: boolean;
  };
  member: {
    items: ConnectionWithTotalCount<MemberItem>;
    onViewAll: () => void;
    isLoading: boolean;
  };
  filterSets: {
    items: ConnectionWithTotalCount<SearchFilterSetItem>;
    isLoading: boolean;
    handleDelete: (id: string) => void;
  };
}
export const IncrementalSearchResultList: React.FC<Props> = ({ query, noteAndComment, folder, member, filterSets }) => {
  const intl = useIntl();

  const [isItemSelected, isSubmitting, isSelected] = useSelectListItemByKey([
    filterSets.items.edges.length,
    member.items.edges.length,
    folder.items.edges.length,
    noteAndComment.items.edges.length,
  ]);

  useEffect(() => {
    if (isSubmitting && !isSelected) {
      noteAndComment.onViewAll();
    }
  }, [isSubmitting, isSelected, noteAndComment]);

  const shouldShowFilterSets = !filterSets.isLoading && filterSets.items.totalCount > 0;
  const shouldShowFolders = !folder.isLoading && folder.items.totalCount > 0;
  const shouldShowMembers = !member.isLoading && member.items.totalCount > 0;

  const isAllLoading = filterSets.isLoading && member.isLoading && folder.isLoading && noteAndComment.isLoading;

  return (
    <>
      <WithLoading isLoading={isAllLoading} spaceY={20}>
        <WithLoading isLoading={filterSets.isLoading}>
          {shouldShowFilterSets && (
            <SearchBoxDropdownSection title={intl.formatMessage({ id: "searchFilterSet.label" })}>
              <SavedSearchFilterSetList
                listIndex={0}
                items={filterSets.items}
                query={query}
                listItemSelection={{ isItemSelected, isSubmitting }}
                onDelete={filterSets.handleDelete}
              />
            </SearchBoxDropdownSection>
          )}
        </WithLoading>
        <WithLoading isLoading={member.isLoading || folder.isLoading || noteAndComment.isLoading} spaceY={20}>
          {shouldShowMembers && (
            <SearchBoxDropdownSection title={intl.formatMessage({ id: "incrementalSearchResult.member" })}>
              <MemberList
                listIndex={1}
                items={member.items}
                // eslint-disable-next-line react/jsx-handler-names
                onViewAll={member.onViewAll}
                listItemSelection={{ isItemSelected, isSubmitting }}
                query={query}
              />
            </SearchBoxDropdownSection>
          )}
          {shouldShowFolders && (
            <SearchBoxDropdownSection title={intl.formatMessage({ id: "incrementalSearchResult.folder" })}>
              <FolderList
                listIndex={2}
                items={folder.items}
                // eslint-disable-next-line react/jsx-handler-names
                onViewAll={folder.onViewAll}
                listItemSelection={{ isItemSelected, isSubmitting }}
              />
            </SearchBoxDropdownSection>
          )}
          <SearchBoxDropdownSection
            title={noteAndCommentListTitle(intl, noteAndComment.items.totalCount, noteAndComment.isLoading)}
          >
            <NoteAndCommentList
              listIndex={3}
              items={noteAndComment.items}
              // eslint-disable-next-line react/jsx-handler-names
              onViewAll={noteAndComment.onViewAll}
              listItemSelection={{
                isItemSelected,
                isSubmitting,
              }}
            />
          </SearchBoxDropdownSection>
        </WithLoading>
      </WithLoading>
    </>
  );
};
